import type { ReactNode } from 'react';
import { makeStyles } from '@material-ui/core';
import { formatDuration } from '../catalog/pipeline/format';
import { formatRate, type Figures } from './figures';

const useStyles = makeStyles(theme => ({
  bar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    // On a phone two to a line, rather than one per line down the screen.
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    border: '1px solid var(--ui-border)',
    borderRadius: 8,
    background: 'var(--ui-bg-elevated)',
    overflow: 'hidden',
  },
  cell: {
    padding: theme.spacing(1.5, 2),
    minWidth: 0,
    borderRight: '1px solid var(--ui-border)',
    '&:last-child': { borderRight: 'none' },
    [theme.breakpoints.down('xs')]: {
      '&:nth-child(2)': { borderRight: 'none' },
      '&:nth-child(-n+2)': { borderBottom: '1px solid var(--ui-border)' },
    },
  },
  label: { fontSize: '0.8rem', color: 'var(--ui-text-muted)' },
  value: {
    fontSize: '1.5rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.4,
  },
  sub: { fontSize: '0.8rem', color: 'var(--ui-text-muted)' },
  // What a figure says when there is nothing to count it over: text, not a
  // number, so it cannot be read as one.
  none: {
    fontSize: '0.9rem',
    fontWeight: 400,
    color: 'var(--ui-text-muted)',
    lineHeight: 2.1,
  },
  definitions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'minmax(0, 1fr)' },
    gap: theme.spacing(1, 4),
    margin: theme.spacing(1.5, 0, 0),
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    lineHeight: 1.6,
    '& dt': { fontWeight: 600, color: 'var(--ui-text)' },
    '& dd': { margin: 0 },
  },
}));

const Cell = ({ label, children }: { label: string; children: ReactNode }) => {
  const classes = useStyles();
  return (
    <div className={classes.cell} role="group" aria-label={label}>
      <div className={classes.label}>{label}</div>
      {children}
    </div>
  );
};

/**
 * The four figures over the list, and what the two easily misread ones count
 * (specs/deployments.md AC-6, AC-7, AC-9).
 *
 * Each label carries its window, because a figure without one cannot be
 * compared with anything. The definitions stand beside the figures rather than
 * behind a tooltip: the change failure rate is only as good as its definition,
 * and a definition a reader has to hover to find is one most never read.
 */
export const FiguresBar = ({
  figures,
  windowDays,
}: {
  figures: Figures;
  windowDays: number;
}) => {
  const classes = useStyles();
  const days = `${windowDays} d`;
  const { finished, failed } = figures.production;

  return (
    <section aria-label="Figures">
      <div className={classes.bar}>
        <Cell label={`Deploys · ${days}`}>
          <div className={classes.value}>{figures.deploys}</div>
        </Cell>
        <Cell label={`Change failure rate · ${days}`}>
          {finished > 0 ? (
            <>
              <div className={classes.value}>
                {formatRate(failed, finished)}
              </div>
              <div className={classes.sub}>
                {failed} of {finished} production{' '}
                {finished === 1 ? 'deploy' : 'deploys'}
              </div>
            </>
          ) : (
            <div className={classes.none}>No finished production deploys</div>
          )}
        </Cell>
        <Cell label={`Median duration · ${days}`}>
          {figures.medianSec !== null ? (
            <div className={classes.value}>
              {formatDuration(figures.medianSec)}
            </div>
          ) : (
            <div className={classes.none}>No finished deploys</div>
          )}
        </Cell>
        <Cell label={`Repos deploying · ${days}`}>
          <div className={classes.value}>{figures.repos}</div>
        </Cell>
      </div>
      <dl className={classes.definitions}>
        <div>
          <dt>Change failure rate</dt>
          <dd>
            The share of finished deploys to production in the last {windowDays}{' '}
            days whose run failed. Cancelled and running deploys count neither
            way, and deploys to QM or test are rehearsals, not production. A
            deploy that succeeded and then broke production is not visible here
            — that needs incident data the portal does not have yet.
          </dd>
        </div>
        <div>
          <dt>Environment</dt>
          <dd>
            The one the workflow names in its file or its name. A workflow that
            names none deploys to production, which is shown with a dashed
            outline as derived.
          </dd>
        </div>
      </dl>
    </section>
  );
};
